import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from '../common/ids.js';
import { PinSchema } from '../common/pin.js';
import { RoleSchema, LocaleSchema } from '../common/enums.js';
import { PermissionSchema } from '../common/permissions.js';
/** `deviceInfo` shape (not specified verbatim in 03-API-CONTRACT.md §1; matches
 * `devices.user_agent` / `devices.platform_hint` from 02-DATA-MODEL.md §3). */
export const DeviceInfoSchema = z.object({
    userAgent: z.string().min(1),
    platformHint: z.string().min(1).optional(),
});
export const LoginRequestSchema = z.object({
    employeeNumber: z.string().min(1),
    pin: PinSchema,
    deviceUuid: UuidSchema,
    deviceInfo: DeviceInfoSchema,
});
/**
 * The login failure codes from 03-API-CONTRACT.md §1. All return an
 * identical generic message and comparable timing (D-05) — never
 * distinguishable by the caller, only by the machine `code`.
 */
export const LOGIN_FAILURE_CODES = [
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'ACCOUNT_HARD_LOCKED',
    'ACCOUNT_INACTIVE',
    'DEVICE_NOT_REGISTERED',
];
export const LoginFailureCodeSchema = z.enum(LOGIN_FAILURE_CODES);
/**
 * The public user shape as embedded in the login/`me` response.
 *
 * `.strict()` is load-bearing twice over: it keeps `pin_hash` off the wire,
 * and (Phase 4) it makes an emitter or fixture still carrying
 * `isPlatformAdmin` fail loudly instead of silently shipping a stale field.
 * The caller's own role is `role` — one system-wide value, `platform_admin`
 * included — and it is what both apps read to decide which portal a person
 * belongs in.
 */
export const AuthUserSchema = z
    .object({
    id: UuidSchema,
    employeeNumber: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    locale: LocaleSchema,
    role: RoleSchema,
    /**
     * The caller's own contact info — the two fields `PATCH /me/profile` may
     * change (P-05). Same types as `UserSchema`'s, because it is the same
     * column read through a different door: `''` is not a value, `null` is
     * "none on file".
     *
     * Required-and-nullable rather than `.optional()`, exactly like
     * `photoUpdatedAt`: a self-service form that cannot read back what it just
     * saved is write-only, and an emitter that forgets these must fail loudly
     * here rather than render an empty field forever.
     */
    email: z.string().min(1).nullable(),
    phone: z.string().min(1).nullable(),
    /**
     * When the caller's own profile photo was attached; `null` = none
     * (Phase 6, P-10). **Never a URL** — presigned GETs expire. The PWA's
     * first-login photo prompt is derived from `role === 'worker' &&
     * photoUpdatedAt === null` (P-01), which is why it rides the login/`me`
     * shape rather than needing a second request.
     */
    photoUpdatedAt: IsoDateTimeSchema.nullable(),
})
    .strict();
/**
 * A membership as surfaced to the caller themselves (login/`me`), which is
 * why it is denormalized with `contractName` — the PWA never joins to find
 * out which contract it is looking at. This differs from the admin-facing
 * membership entity in `users/membership.ts`, which is keyed by id and has
 * no need to repeat the contract's name.
 *
 * Scope only (Phase 4): `siteIds: null` = every site of the contract. The
 * role is on `user`, not here.
 */
export const AuthMembershipSchema = z.object({
    contractId: UuidSchema,
    contractName: z.string().min(1),
    siteIds: z.array(UuidSchema).nullable(),
});
export const AuthDeviceSchema = z.object({
    id: UuidSchema,
    /** `true` only on the first-device auto-bind (D-06). */
    boundNow: z.boolean(),
});
export const LoginResponseSchema = z.object({
    accessToken: z.string().min(1),
    mustChangePin: z.boolean(),
    user: AuthUserSchema,
    memberships: z.array(AuthMembershipSchema),
    permissions: z.array(PermissionSchema),
    /** `null` when `device_policy = off` — no device is bound (Phase 1
     * change batch, 2026-08-27, Foundation). */
    device: AuthDeviceSchema.nullable(),
});
//# sourceMappingURL=login.js.map